import React, { useState } from 'react';
import { Modal, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, RotateCw } from 'lucide-react';
import { TipBox } from '../onboarding/components/TipBox';
import {
  CRM_TRADEMARK_NOTICE,
  MANUAL_CONNECT_CRMS,
  ManualConnectCrmId,
  ManualCrmField,
  ROLLOUT_CRMS,
} from '../../../constants/rolloutCrms';
import type { Organization } from '../../../types/organization.types';
import { saveCrmConnectSecrets } from '../../../api/organizationSecrets';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { useOnboardingWebhook } from '../onboarding/hooks/useOnboardingWebhook';
import {
  ManualCrmValues,
  manualCrmFieldLabel,
  cleanManualCrmValues,
  getCrmConnectRequest,
  validateManualCrmValues,
  withCrmConnectRequest,
} from '../../../utils/manualCrmConnect';

const FIELD_PLACEHOLDERS: Record<ManualCrmField, string> = {
  site_url: 'https://yoursite.com',
  api_key: 'Enter your API Key',
  username: 'Username or email',
  password: 'Enter password',
};

// Matches Rollout's connector list and connector view (Mantine): square tiles,
// labelled inputs and a blue Connect button.
const TILE_CLASS =
  'relative flex flex-col justify-center aspect-square w-full max-w-[160px] p-3 rounded-lg border border-black/10 dark:border-white/10 shadow-[0_0_6px_#0000000f] hover:shadow-[0_0_10px_#00000026] transition-shadow text-center';
const INPUT_CLASS =
  'block w-full h-10 rounded-lg border px-3 pr-10 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 outline-none focus:border-[#228be6]';
const SAVE_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-2 h-10 px-5 rounded-lg bg-[#e7f5ff] hover:bg-[#d0ebff] dark:bg-[#228be6]/20 text-[#1864ab] dark:text-[#74c0fc] text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed';
// Secret fields start hidden, with an eye toggle.
const SECRET_FIELDS: readonly ManualCrmField[] = ['api_key', 'password'];

interface ManualCrmConnectModalProps {
  crm: ManualConnectCrmId;
  organization: Organization;
  /** Where the client connected from, for the team's notification. */
  source: 'onboarding' | 'client_admin';
  onClose: () => void;
  /** Called after the request is saved, e.g. to refresh the org. */
  onSubmitted?: () => void;
}

/**
 * Connect popup for CRMs the team connects by hand. It looks like the Rollout
 * Connect popup (the "{CRM} Setup" modal, the CRM Integrations card, the
 * connector tile, then the connector form). Saves what the client entered to
 * Secret Manager, records the request on the org, and sends it to the
 * Integrations webhook for the team. Render it only while open, so each open
 * starts on the tile.
 */
export const ManualCrmConnectModal: React.FC<ManualCrmConnectModalProps> = ({
  crm,
  organization,
  source,
  onClose,
  onSubmitted,
}) => {
  const { showToast } = useToast();
  const { sendWebhook } = useOnboardingWebhook();
  const { updateSettings } = useUpdateOrganizationSettings();
  const [view, setView] = useState<'list' | 'connect'>('list');
  const [revealed, setRevealed] = useState<Partial<Record<ManualCrmField, boolean>>>({});
  const [values, setValues] = useState<ManualCrmValues>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(
    getCrmConnectRequest(organization.onboardingData, crm)?.submitted_at
  );

  const label = ROLLOUT_CRMS[crm].label;
  const { icon, fields, helperText } = MANUAL_CONNECT_CRMS[crm];
  const errors = showErrors ? validateManualCrmValues(crm, values) : {};

  const handleConnect = async (event: React.FormEvent) => {
    event.preventDefault();
    if (Object.keys(validateManualCrmValues(crm, values)).length > 0) {
      setShowErrors(true);
      return;
    }
    const cleaned = cleanManualCrmValues(crm, values);
    const now = new Date().toISOString();
    setIsSubmitting(true);
    try {
      await saveCrmConnectSecrets(organization.id, crm, cleaned);
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: withCrmConnectRequest(organization.onboardingData, crm, now),
        } as Partial<Organization>,
      });
      sendWebhook(
        'CRM Connect Request',
        { crm, crm_label: label, source, ...cleaned },
        organization
      ); // Fire and forget
      setSubmittedAt(now);
      setValues({});
      setShowErrors(false);
      onSubmitted?.();
    } catch (error) {
      console.error('Error submitting CRM connection:', error);
      showToast({ message: `Couldn't connect ${label}. Please try again.` });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <Modal.Header>
        <Typography variant="h3">{label} Setup</Typography>
      </Modal.Header>
      <Modal.Body className="pb-8">
        <div className="border border-gray-300 dark:border-white/15 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full flex-shrink-0">
              <RotateCw className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <Typography variant="h5" className="font-semibold">
              CRM Integrations
            </Typography>
          </div>
          {!submittedAt && (
            <div className="mb-3">
              <TipBox>
                <strong>Pro tip:</strong> Before connecting your CRM, we highly recommend applying
                the{' '}
                <code className="bg-yellow-200 dark:bg-yellow-800/40 px-1 rounded">
                  speculo_sync
                </code>{' '}
                tag to the contacts you want Remi (AI) to engage with to help speed up the initial
                sync process especially for large databases. This tag does not trigger any
                communication. You can still move forward without applying the tag. Additional
                contacts can be synced over at any time after the integration is established.
              </TipBox>
            </div>
          )}

          {view === 'list' ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-4 mt-6">
              <button type="button" onClick={() => setView('connect')} className={TILE_CLASS}>
                <img src={icon} alt="" className="block mx-auto w-12 aspect-square" />
                <span className="mt-3.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {label}
                </span>
                {submittedAt && (
                  <span className="mt-1 text-xs text-green-700 dark:text-green-400">Submitted</span>
                )}
              </button>
            </div>
          ) : (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-4">
                <button
                  type="button"
                  aria-label="Go Back to Integrations"
                  onClick={() => setView('list')}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-white/10"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <img src={icon} alt={label} className="block w-7" />
              </div>

              {submittedAt && (
                <div className="flex items-center gap-3 mb-4 rounded-lg border border-black/10 dark:border-white/10 p-3 shadow-[0_0_6px_#0000000f]">
                  <img src={icon} alt="" className="w-8 h-8" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {label} submitted
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Our team is finishing the connection and will let you know when it&apos;s
                      done.
                    </p>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
              )}

              <form onSubmit={handleConnect} noValidate className="space-y-4">
                {fields.map((field, index) => {
                  const secret = SECRET_FIELDS.includes(field);
                  return (
                    <div key={field}>
                      <label
                        htmlFor={`manual-crm-${field}`}
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-100"
                      >
                        {manualCrmFieldLabel(crm, field)}
                      </label>
                      {index === 0 && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {helperText}
                        </p>
                      )}
                      <div className="relative mt-1.5">
                        <input
                          id={`manual-crm-${field}`}
                          value={values[field] ?? ''}
                          onChange={(e) =>
                            setValues((prev) => ({ ...prev, [field]: e.target.value }))
                          }
                          type={secret && !revealed[field] ? 'password' : 'text'}
                          autoComplete={secret ? 'new-password' : 'off'}
                          placeholder={FIELD_PLACEHOLDERS[field]}
                          aria-invalid={Boolean(errors[field])}
                          className={`${INPUT_CLASS} ${
                            errors[field]
                              ? 'border-red-500'
                              : 'border-gray-300 dark:border-white/15'
                          }`}
                        />
                        {secret && (
                          <button
                            type="button"
                            aria-label={revealed[field] ? 'Hide' : 'Show'}
                            onClick={() =>
                              setRevealed((prev) => ({ ...prev, [field]: !prev[field] }))
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400"
                          >
                            {revealed[field] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                      {errors[field] && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors[field]}</p>
                      )}
                    </div>
                  );
                })}
                <div className="pt-1">
                  <button type="submit" disabled={isSubmitting} className={SAVE_BUTTON_CLASS}>
                    {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submittedAt ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{CRM_TRADEMARK_NOTICE}</p>
      </Modal.Body>
    </Modal>
  );
};
